import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import moment from "moment";
import {ConfigProvider, message} from "antd";
import {DeclineInvoiceModal} from "../AdminInvoices/DeclineInvoiceModal";
import AdminDeliveryProviderMonthDeliveriesModal from "./AdminDeliveryProviderMonthDeliveriesModal/AdminDeliveryProviderMonthDeliveriesModal";
import AdminDeliveryProviderInvoiceModal from "./AdminDeliveryProviderInvoiceModal/AdminDeliveryProviderInvoiceModal";
import getColumns from "./columns";
import PageTitleHeader from "../../components/PageTitle";
import {TableDateRangeTable} from "../../components/OrdersDateRangeTable/TableDateRangeTable";
import {TableDateSearchContext} from "../Context/TableDateSearchContext";
import {useRequest} from "../../utils/hooks";
import {HttpClient} from "../../http/HttpClient";
import {InvoicesStatusToNameAndColor, InvoiceStatusTypes} from "../../provider/ProviderManageInvoices/consts";
import "../AdminInvoices/admin-invoices.css";

const AdminDeliveryProviderInvoices = () => {
    const {searchDate} = useContext(TableDateSearchContext);
    const [deliveryProviderOrdersModal, setDeliveryProviderOrdersModal] = useState(null);
    const [deliveryProviderInvoiceModal, setDeliveryProviderInvoiceModal] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const [innerInvoices, setInnerInvoices] = useState([]);
    const [declineModalInvoiceId, setDeclineModalInvoiceId] = useState(null);

    const [invoices, loadingInvoices, error] = useRequest(
        `/admin/api/deliveryProviderInvoice/byMonth/${moment(searchDate[0]).format("MM-YYYY")}`,
        "GET",
        null,
        [searchDate],
        !!searchDate,
        null,
        []
    );

    useEffect(() => {
        if (invoices?.length) {
            setInnerInvoices(invoices);
        }
    }, [invoices]);

    useEffect(() => {
        if (error) {
            console.error("Error fetching delivery provider invoices:", error);
            message.error("שגיאה בטעינת חשבוניות ספקי שילוח");
        }
    }, [error]);

    const filteredInvoices = useMemo(
        () =>
            searchInput
                ? innerInvoices?.filter(
                      invoice =>
                          invoice.deliveryProvider?.name?.includes(searchInput) ||
                          invoice.deliveryProvider?.email?.includes(searchInput)
                  )
                : innerInvoices,
        [searchInput, innerInvoices]
    );

    const totalInvoicesPrice = useMemo(
        () => (filteredInvoices ?? []).reduce((acc, invoice) => acc + (invoice?.price ?? 0), 0),
        [filteredInvoices]
    );

    const updateStatus = useCallback(
        async (invoiceId, statusId, declineMessage = null) => {
            const updatedInvoice = await HttpClient.safePost(
                `/admin/api/deliveryProviderInvoice/changeStatus/${invoiceId}`,
                {
                    status: statusId,
                    declineMessage
                }
            );

            if (updatedInvoice.error) {
                message.error("שגיאה בעת עדכון סטטוס חשבונית");
                message.error(updatedInvoice.error);
                return;
            }

            message.success(`סטאטוס החשבונית שונה ל${InvoicesStatusToNameAndColor[statusId].name}`);
            setInnerInvoices(prev =>
                prev.map(invoice =>
                    invoice.invoiceId === invoiceId
                        ? {
                              ...invoice,
                              status: updatedInvoice.status,
                              declineMessage: updatedInvoice.declineMessage
                          }
                        : invoice
                )
            );

            if (deliveryProviderInvoiceModal) {
                setDeliveryProviderInvoiceModal(updatedInvoice);
            }
        },
        [deliveryProviderInvoiceModal]
    );

    const onChangeInvoiceStatus = useCallback(
        async (invoiceId, statusId) => {
            if (statusId === InvoiceStatusTypes.DECLINED) {
                setDeclineModalInvoiceId(invoiceId);
            } else {
                await updateStatus(invoiceId, statusId);
            }
        },
        [updateStatus]
    );

    const onDeclineInvoice = useCallback(
        async values => {
            await updateStatus(declineModalInvoiceId, InvoiceStatusTypes.DECLINED, values.declineMessage);
            setDeclineModalInvoiceId(null);
        },
        [updateStatus, declineModalInvoiceId]
    );

    const onAddNewInvoice = useCallback(
        newInvoice => {
            if (moment(newInvoice.month).isSame(deliveryProviderOrdersModal.month, "month")) {
                setInnerInvoices(prev => [
                    ...prev,
                    {
                        ...newInvoice,
                        deliveryProvider: newInvoice.deliveryProvider || {
                            name: deliveryProviderOrdersModal.deliveryProviderName,
                            email: deliveryProviderOrdersModal.deliveryProviderEmail
                        }
                    }
                ]);
            }
        },
        [deliveryProviderOrdersModal]
    );

    return (
        <div className="admin-invoices">
            <ConfigProvider direction="rtl">
                <AdminDeliveryProviderMonthDeliveriesModal
                    deliveryProviderId={deliveryProviderOrdersModal?.deliveryProviderId}
                    visible={!!deliveryProviderOrdersModal}
                    invoices={
                        deliveryProviderOrdersModal
                            ? innerInvoices?.filter(
                                  ({deliveryProviderId}) =>
                                      deliveryProviderId === deliveryProviderOrdersModal.deliveryProviderId
                              )
                            : []
                    }
                    onCancel={() => setDeliveryProviderOrdersModal(null)}
                    addInvoice={onAddNewInvoice}
                    month={deliveryProviderOrdersModal?.month}
                />

                <AdminDeliveryProviderInvoiceModal
                    visible={deliveryProviderInvoiceModal}
                    invoice={deliveryProviderInvoiceModal}
                    updateStatus={(statusId, declineMessage) =>
                        updateStatus(deliveryProviderInvoiceModal.invoiceId, statusId, declineMessage)
                    }
                    onCancel={() => setDeliveryProviderInvoiceModal(null)}
                    onRemove={() => {
                        setInnerInvoices(prev =>
                            prev.filter(inv => inv.invoiceId !== deliveryProviderInvoiceModal.invoiceId)
                        );
                        setDeliveryProviderInvoiceModal(null);
                    }}
                />

                <DeclineInvoiceModal
                    visible={declineModalInvoiceId}
                    onClose={() => setDeclineModalInvoiceId(null)}
                    onSave={onDeclineInvoice}
                />

                <PageTitleHeader showBack={false}>חשבוניות ספקי שילוח</PageTitleHeader>
                <TableDateRangeTable
                    data={filteredInvoices}
                    onInputSearch={setSearchInput}
                    isLoadingOrderTable={loadingInvoices}
                    tableClassName="admin-invoices-table"
                    showSearchByCreatedAt={false}
                    hideRangePicker={true}
                    rowKey="invoiceId"
                    statisticsLabel={
                        <div className="statistics-label">
                            <span>סה״כ חוב לספקי שילוח: {totalInvoicesPrice?.toLocaleString()} ₪</span>
                        </div>
                    }
                    columns={getColumns({
                        openDeliveryProviderOrders: setDeliveryProviderOrdersModal,
                        openInvoice: setDeliveryProviderInvoiceModal,
                        updateStatus: onChangeInvoiceStatus
                    })}
                />
            </ConfigProvider>
        </div>
    );
};

export default AdminDeliveryProviderInvoices;
